import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { getMainDefinition } from '@apollo/client/utilities';
import { filter } from 'rxjs/operators';

import { ApolloClient, InMemoryCache, gql, split, HttpLink } from '@apollo/client';
import cookieSetter from './cookieSetter';
import sessionController from './sessionController';
import gqlSchemaLibrary from "./graphql.schemas";
import {BehaviorSubject} from "rxjs";
import HistoryState from "./history.state";


class GraphQlClientService {
    constructor() {
        this.userSubject = sessionController.userSubject;
        this.orgSubject = new BehaviorSubject();
        const authLink = setContext((_, { headers }) => {
            const token = sessionController.token;
            return {
                headers: {
                    ...headers,
                    authorization: token ? `${token}` : ''
                }
            }
        })
        // Create an http link:
        const httpLink = new HttpLink({
            uri: process.env.REACT_APP_HTTP_URI,
            // uri: 'http://localhost:4000/graphql',
        });

        const authHttpLink = authLink.concat(httpLink);

        // Create a WebSocket link:
        const wsLink = new GraphQLWsLink(createClient({
            url: process.env.REACT_APP_WS_URI,
        }));

        // depending on what kind of operation is being sent
        const link = split(
            // split based on operation type
            ({ query }) => {
                const definition = getMainDefinition(query);
                return (
                    definition.kind === 'OperationDefinition' &&
                    definition.operation === 'subscription'
                );
            },
            wsLink,
            authHttpLink,
        );

        const logoutLink = onError(({ graphQLErrors, networkError }) => {
            if (graphQLErrors && graphQLErrors.length !== 0) {
               for (const error of graphQLErrors) {
                   if ('code' in error.extensions) {
                       if ( error.extensions.code === 'UNAUTHENTICATED') {
                           this.logout();
                       }
                   }
               }
            }
        });

        this._client = new ApolloClient({
            link: logoutLink.concat(link),
            cache: new InMemoryCache(),
        });

        sessionController.inactivityTimeOutSubject.subscribe(() => {
            this.refreshToken(sessionController.lastActivity);
        });

        sessionController.timeoutSubject.subscribe(() => {
            this.logout();
        });
    }

    __getCurrentUser() {
        return this._client.query({
            query: gql`{
                me {
                    _id
                    selectedOrgId
                    email
                    bio {
                        lastname
                        firstname
                        gender
                    }
                    profiles {
                        clinicianId
                    }
                    clinician {
                        role
                    }
                    userRoles {
                        alertsPreference {
                          read
                          edit
                          create
                        }
                        devicesAssign {
                            read
                            edit
                            create
                        }
                        programsBase  {
                            read
                            edit
                            create
                        }
                        programsEnroll  {
                            read
                            edit
                            create
                        }
                        programsRules  {
                            read
                            edit
                            create
                        }
                        userClinicians  {
                            read
                            edit
                            create
                        }
                        userPatients  {
                            read
                            edit
                            create
                        }
                        userCredentials  {
                            read
                            edit
                            create
                        }
                    }
                    expiredIn
                }
            }`
        })
            .then((response) => {
                console.log('[GQL] Currrent User: ', response.data.me);
                if (response.data.me) {
                    sessionController.currentSession(response.data.me, response.data.me.expiredIn);
                } else {
                    this.logout();
                }
                return response.data.me;
            })
            .catch((e) => {
                console.error(`Error pulling "ME" method, you're out!`, e);
                this.logout();
                return null
            })
    }

    __getCurrentOrg() {
        return this._client.query({
            query: gqlSchemaLibrary.organizations.query.getCurrentOrg
        }).then((response) => {
            this.orgSubject.next(response.data.getCurrentOrg);
        }).catch((e) => {
            return null
        });
    }

    resetStore() {
        window.location.reload();
        /*
        // >> TROUBLESHOOT THIS
        this.client.resetStore()
        .then((queryResult) => {
            console.log('queryResult: ', queryResult);
            this.__getCurrentUser();
        });
        */
    }

    getCurrentUser() {
        if (!this.userSubject.value) {
            this.__getCurrentUser();
        }
        return this.userSubject.pipe(filter(d => !!d || d === null));
    }

    getCurrentOrg() {
        if (!this.orgSubject.value) {
            this.__getCurrentOrg();
        }
        return this.orgSubject.pipe(filter(d => !!d || d === null));
    }

    get client() {
        return this._client;
    }

    logout(manual) {
        sessionController.deleteSession(manual);
        HistoryState.clearState();
        this.orgSubject.next(null);
        this._client.clearStore();
    }

    login(username, password) {
        // requires external catch
        return this._client.mutate({
            mutation: gql`
            mutation Login($username: String!, $password: String!){
                login(username:$username, password: $password){
                    user{

                        _id
                        selectedOrgId
                        email
                        bio {
                            lastname
                            firstname
                            gender
                        }
                        profiles {
                            clinicianId
                        }
                        clinician {
                            role
                        }
                        userRoles {
                            devicesAssign {
                                read
                                edit
                                create
                            }
                            programsBase  {
                                read
                                edit
                                create
                            }
                            programsEnroll  {
                                read
                                edit
                                create
                            }
                            programsRules  {
                                read
                                edit
                                create
                            }
                            userClinicians  {
                                read
                                edit
                                create
                            }
                            userPatients  {
                                read
                                edit
                                create
                            }
                            userCredentials  {
                                read
                                edit
                                create
                            }
                        }
                    }
                    token
                    isLockedAccount
                    isExpired
                    expiresIn
                }
            }`,
            variables: {
                username,
                password
            }
        })
            .then(({ data }) => {
                console.log('login res: ', data);
                const login = data.login;
                const token = login.token;
                const user = login.user;
                const isLockedAccount = login.isLockedAccount;
                const isExpired = login.isExpired;

                if (!token && !isLockedAccount && !isExpired) {
                    throw new Error('Invalid login token');
                }
                else if (!token && isLockedAccount && !isExpired){
                    throw new Error('locked account');
                }
                else if (!token && !isLockedAccount && isExpired){
                    throw new Error('password expired');
                }
                sessionController.createSession(token, user, login.expiresIn);
                this.__getCurrentOrg();
                return user;
            })
    }

    refreshToken(lastActivity) {
        this._client.mutate({
            mutation: gqlSchemaLibrary.auth.mutation.refreshToken,
            variables: {
                lastActivity: lastActivity
            }
        }).then((response) => {
            if (this.userSubject.value) {
                const data = response.data.refreshToken;
                sessionController.refreshSession(data.token, data.expiresIn);
            } else {
                sessionController.__LogoutOnExpiredSession();
            }
        }).catch((e) => {
            sessionController.__LogoutOnExpiredSession();
            return null
        });
    }
}


const GraphQlClient = (() => {
    const client = new GraphQlClientService();
    return client;
})();
export default GraphQlClient;
